import { Alert, Button, Modal, Separator, Spinner } from '@ntconsult/aluma-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectiveAccess } from '../../hooks/useEffectiveAccess';
import { useSharesForResource } from '../../hooks/useSharesForResource';
import type { ShareResourceType } from '../../types';
import { LinksSection } from './LinksSection';
import { OrgVisibilityToggle } from './OrgVisibilityToggle';
import { PeopleSection } from './PeopleSection';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: ShareResourceType;
  resourceId: string;
  resourceName: string;
}

export function ShareModal({
  isOpen,
  onClose,
  resourceType,
  resourceId,
  resourceName,
}: ShareModalProps) {
  const { t } = useTranslation(['shares', 'common']);
  const titleId = useId();

  const { data: shares, isLoading } = useSharesForResource(resourceType, resourceId, isOpen);
  const { data: effective } = useEffectiveAccess(resourceType, resourceId, isOpen);

  const canManage = !!effective?.canManageShares;

  const userShares = (shares ?? []).filter((s) => s.kind === 'user');
  const groupShares = (shares ?? []).filter((s) => s.kind === 'group');
  const orgShare = (shares ?? []).find((s) => s.kind === 'org') ?? null;
  const linkShares = (shares ?? []).filter((s) => s.kind === 'link');

  return (
    <Modal open={isOpen} onClose={onClose} ariaLabelledBy={titleId} size="xl">
      <Modal.Header
        titleId={titleId}
        title={`${t('shares:title')}: ${resourceName}`}
        onClose={onClose}
      />
      <Modal.Body>
        {!canManage && (
          <Alert variant="info" className="mb-4">
            {t('shares:readOnly')}
          </Alert>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <PeopleSection
              resourceType={resourceType}
              resourceId={resourceId}
              userShares={userShares}
              groupShares={groupShares}
              canManage={canManage}
            />

            <Separator decorative />

            <OrgVisibilityToggle
              resourceType={resourceType}
              resourceId={resourceId}
              orgShare={orgShare}
              canManage={canManage}
            />

            <Separator decorative />

            <LinksSection
              resourceType={resourceType}
              resourceId={resourceId}
              linkShares={linkShares}
              canManage={canManage}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
